<template>
  <div>
    <div class="form-group form-group--two-box">
      <div class="control-box control-box--phone" :class="{ 'invalid dirty': $v.phone.$error }">
        <PhoneInput
          v-model.trim="phone"
          id="phone-auth"
          @blur="$v.phone.$touch"
          :is-label="!isHiddenLabels"
        />
      </div>
      <div
        class="control-box control-box--code"
        v-show="showCodeField || isCodeFieldVisible"
        :class="{ 'invalid dirty': $v.code.$error }"
      >
        <label v-if="!isHiddenLabels" for="code" class="control-box__label">
          {{ $t('auth_code_label') }}
        </label>
        <div class="flex">
          <TheMask
            id="code"
            name="code"
            v-model="code"
            type="text"
            inputmode="numeric"
            mask="####"
            :masked="false"
            :disabled="disableFields || !showCodeField"
            :placeholder="isHiddenLabels ? $t('auth_code_label') : '****'"
            class="control control--code-auth control--input control-box__control"
            ref="code"
            autocomplete="one-time-code"
            @blur.native="$v.code.$touch"
          ></TheMask>
          <div v-show="showLoader" class="loader"></div>
        </div>
      </div>
      <div class="control-box__help-block--wrapper">
        <div
          class="control-box__help-block invalid dirty"
          v-if="$v.code.$error || $v.phone.$error"
        >
          <div v-if="!$v.phone.phoneValidator">
            {{ $t('auth_invalid_code') }}
          </div>
          <div v-if="!$v.phone.minLength || (!$v.code.minLength && showCodeField)">
            {{ $t('notfull_field_error') }}
          </div>
          <div v-if="!$v.phone.required || (!$v.code.required && showCodeField)">
            {{ $t('blank_field_error') }}
          </div>
        </div>
        <p class="control-box__help-block" :class="{ 'invalid dirty': !info.responseStatus }">
          {{ info.responseMessage }}
        </p>
      </div>
    </div>
    <div class="checkbox" v-if="rememberField">
      <input id="remember-me"
             class="checkbox__input visually-hidden"
             v-model="remember"
             type="checkbox" />
      <label class="checkbox__label" for="remember-me">
        <span class="checkbox__text">{{ $t('auth_remember_me') }}</span>
      </label>
    </div>
    <div class="resend-code__wrap" v-if="showResendInfo">
      <div class="resend-code__trigger" :class="{'disabled': disableResendTrigger }" v-if="!$v.phone.$invalid && isPossibleRepeatSendCode" @click="verifyPhone()">{{ $t('auth.resend-sms') }}</div>
      <div class="resend-code__info" v-if="!isPossibleRepeatSendCode">{{ $t('auth.email_resend-sms-text-1') }} <span class="resend-code__info-timer">00:{{ computedTimer }}</span> {{ $t('auth.email_resend-sms-text-2') }}</div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapActions, mapGetters, mapState } from 'vuex'
import { TheMask } from 'vue-the-mask'
import { required, numeric, minLength, helpers } from 'vuelidate/lib/validators'
import PhoneInput from '../inputs/PhoneInput'
import qs from 'qs'
import { GoogleTagManagerService } from '@/services/AnalyticService'
import AuthService from '@/services/AuthService'
import { phoneValidations } from '@/helpers/phoneValidations'

const phoneValidator = helpers.regex(
  'phoneValidator',
  phoneValidations
)

export default {
  name: 'AuthPhone',
  props: {
    authPhoneUrls: {
      type: Object,
      validator: function (obj) {
        if (!obj) return true
        return obj.phoneUrl && obj.checkCodeUrl
      }
    },
    redirectUrl: {
      type: String
    },
    rememberField: {
      type: Boolean,
      default: function () {
        return true
      }
    },
    isAjaxAuth: {
      type: Boolean,
      default: false
    },
    isAuthModal: {
      type: Boolean,
      default: false
    },
    simpleAuth: {
      type: Boolean,
      default: false
    },
    referrer: {
      type: String,
      default: ''
    },
    touchPhoneFlag: {
      type: String,
      default: ''
    },
    isHiddenLabels: {
      type: Boolean,
      default: false
    },
    isCodeFieldVisible: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      disableResendTrigger: false,
      isPossibleRepeatSendCode: true,
      timer: 60,
      timerInterval: null,
      showResendInfo: false,
      phone: '',
      code: '',
      disableFields: false,
      remember: true,
      showCodeField: false,
      showCodeFieldSpinner: false,
      showLoader: false,
      info: {
        responseMessage: null,
        responseStatus: null
      },
      token: '',
      sendCity: false,
      authService: new AuthService(this.authPhoneUrls)
    }
  },
  components: {
    PhoneInput,
    TheMask
  },
  validations: {
    phone: {
      required,
      numeric,
      phoneValidator,
      minLength: minLength(9)
    },
    code: {
      required,
      numeric,
      minLength: minLength(4)
    }
  },
  watch: {
    touchPhoneFlag: function () {
      this.$v.phone.$touch()
    },
    phone: function () {
      if (this.phone.length === 9) {
        this.$v.phone.$touch()
        if (!this.$v.phone.$invalid) {
          this.verifyPhone()
        }
      } else {
        this.showCodeField = false
      }
    },
    code: function () {
      if (this.code.length === 4) {
        this.verifyCode()
      }
    }
  },
  methods: {
    ...mapActions('contactsNew', [
      'SET_CONTACTS_DATA'
    ]),
    ...mapActions('deliveryNew', [
      'SET_DELIVERY_CITY',
      'SET_DELIVERY_IS_OPEN'
    ]),
    ...mapActions([
      'FETCH_USER_DATA',
      'SET_CONTACTS_FORM_FIELDS_FROM_SERVER',
      'GET_FULL_CART',
      'SET_IS_MANAGER_LOGGED',
      'SET_MANAGERS_CLIENT_DATA_ACTION',
      'FETCH_LEGAL_PERSON_INFO',
      'FETCH_CART_BONUSES_INFO',
      'FETCH_INITIAL_INFO',
      'CHANGE_MANAGER_TYPE',
      'CHANGE_WELCOME_BONUS_STATUS'
    ]),
    processClientData (data) {
      let { user_data: userData } = data
      if (data.user_city_location && Object.keys(data.user_city_location).length) {
        const { user_city_location: userCity } = data
        userData = { ...userData, city: { name: this.isUkLanguage ? userCity.name_uk : userCity.name_ru, id: userCity.city_id } }
        this.SET_DELIVERY_CITY({ ...data.user_city_location, name: this.isUkLanguage ? userCity.name_uk : userCity.name_ru, id: userCity.city_id })
      } else {
        this.SET_DELIVERY_IS_OPEN(true)
      }
      this.SET_IS_MANAGER_LOGGED(true)
      this.SET_MANAGERS_CLIENT_DATA_ACTION({ ...userData })
      // this.SET_CONTACTS_FORM_FIELDS_FROM_SERVER(userData)
      this.SET_CONTACTS_DATA(userData)
      this.FETCH_LEGAL_PERSON_INFO()
      // this.FETCH_CART_BONUSES_INFO({ client_phone: '380' + this.phone })
      this.GET_FULL_CART()
    },
    verifyPhone () {
      const phone = '380' + this.phone
      this.disableResendTrigger = true

      this.authService.verifyPhone(phone)
        .then(response => {
          this.info.responseMessage = response.message
          if (response.status === 'success' || response.status === 'need_verification') {
            this.info.responseStatus = true
            this.showCodeField = true
            this.$nextTick(() => this.$refs.code.$el.focus())
            this.sendCity = response.send_city
          } else if (response.status === 'ok') {
            this.processClientData(response)
          } else {
            this.info.responseStatus = false
            this.showCodeField = false
          }
        })
        .catch(error => {
          this.info.responseMessage = error.response?.message ?? this.$t('error_occurred')
        })
        .finally(() => {
          this.startTimer()
        })
    },
    startTimer () {
      this.showResendInfo = true
      this.isPossibleRepeatSendCode = false
      this.timer = 60
      this.timerInterval = setInterval(() => {
        this.timer--
        if (this.timer < 1) {
          clearInterval(this.timerInterval)
          this.isPossibleRepeatSendCode = true
          this.disableResendTrigger = false
        }
      }, 1000)
    },
    verifyCode () {
      this.showLoader = true
      this.disableFields = true

      const { remember, code } = this

      this.authService.verifyCode({ code, remember })
        .then(response => {
          this.info.responseMessage = response.message
          if (response.status === 'success') {
            localStorage.removeItem('redirect-route')

            // eslint-disable-next-line no-return-assign
            const redirect = () => (this.redirectUrl ? (location.href = this.redirectUrl) : location.reload())
            const hasWelcomeBonus = response.has_welcome_bonus
            const askAboutLoginType = response.ask_about_login_type
            const managerType = response.manager_type

            if (this.isAjaxAuth || askAboutLoginType || (hasWelcomeBonus && !this.simpleAuth)) {
              this.FETCH_USER_DATA({ fields: ['name', 'phone', 'last_name', 'father_name', 'email', 'social', 'birthday', 'gender', 'language', 'subscriber'] }).then(response => {
                if (response.user_data) this.SET_CONTACTS_FORM_FIELDS_FROM_SERVER(response.user_data)
                if (response.user_data) this.SET_CONTACTS_DATA(response.user_data)

                const { user_id: userId, user_type: userType } = response.user_data

                this.sendUserInfo(userId, userType)

                this.FETCH_INITIAL_INFO().then(() => {
                  if (this.referrer === 'wait-list') {
                    window.dispatchEvent(new CustomEvent('waitListTrigger'))
                  }

                  if (this.referrer === 'wish-list') {
                    window.dispatchEvent(new CustomEvent('wishListTrigger'))
                  }

                  if (askAboutLoginType && !this.$constants.IS_CHECKOUT()) {
                    this.CHANGE_MANAGER_TYPE(managerType)

                    this.$modal.show('user-type-modal', {
                      isChangeUserType: true,
                      redirect: redirect
                    })
                  }

                  if (hasWelcomeBonus && this.isAuthModal) {
                    this.$modal.show('welcome-bonus-modal')
                  }

                  this.CHANGE_WELCOME_BONUS_STATUS(hasWelcomeBonus)
                })
              })

              if (this.isAuthModal) {
                this.$modal.hide('auth-modal')
              }
            } else {
              if (this.selectedUserGeo && this.sendCity) {
                axios
                  .post(this.urlLanguagePrefix + '/location/', qs.stringify({ city_id: this.selectedUserGeo.city_id }))
                  .then(() => redirect())
              } else redirect()
            }
          } else if (response.status === 'ok') { // логика менеджерского чекаута
            localStorage.removeItem('redirect-route')
            this.processClientData(response.data)
          } else {
            this.info.responseStatus = false
            this.showLoader = false
            this.disableFields = false
          }
          this.$emit('endPhoneVerification')
        })
        .catch(error => {
          this.info.responseMessage = error.response.message
          this.info.responseStatus = false
          this.disableFields = false
        })
        .finally(() => {
          this.showLoader = false
        })
    },
    sendUserInfo (userId, userType) {
      GoogleTagManagerService.pushToDatalayer({
        'UID': userId.toString(),
        'UserType': userType,
        'event': 'Auth'
      })
    }
  },
  computed: {
    ...mapGetters(['urlLanguagePrefix', 'isUkLanguage']),
    ...mapState({
      selectedUserGeo: state => state.user.selectedUserGeo
    }),
    computedTimer () {
      return this.timer <= 9 ? '0' + this.timer : this.timer
    }
  }
}
</script>

<style scoped lang="sass">

.form-group
  margin-bottom: 15px
.control-box
  margin-bottom: 0
.control-box.control-box--phone
  width: 56%
  margin-right: 4%
.control-box.control-box--code
  width: 36%
  margin-right: 0
.control--code-auth
  max-width: 110px
.control-box__help-block, .control-box__help-block--wrapper
  width: 100%
.flex
  display: flex
.loader
  display: block
  margin: 0 0 0 10px
  flex-shrink: 0

// стили для checkout
.control-box__label
  font-size: 14px
.control--phone-auth
  font-size: 16px

.checkbox__label
  color: $text-color

  &:hover, &:focus
    &:before
      border-color: $placeholder-color!important

.contact-form__auth-form .control-box.control-box--phone
  +mmin(1340)
    max-width: 295px
.resend-code
  &__wrap
    padding-top: 10px
  &__trigger
    cursor: pointer
    color: #3e77aa
    font-size: 14px
    &.disabled
      pointer-events: none
  &__info
    margin: 10px 0
    font-size: 12px
    color: #909090
    text-align: center
    background: rgba(62, 119, 170, .1)
    padding: 15px
    border-radius: 3px
    font-weight: 400
    &-timer
      width: 34px
      font-weight: 700
      display: inline-block
</style>
